/**
 * Icons for the features listed in the plan-change warning.
 *
 * The list is backend-driven: `validateChangePlan` returns each lost feature's `description`
 * straight from the `productPlans` doc, so these keys are those exact strings. A miss is fine and
 * expected — `getPlanWarningIcon` falls back to a neutral mark, so a new or renamed plan feature
 * degrades to a plain row instead of breaking the dialog.
 *
 * Kept separate from `plan-feature-copy.ts` on purpose: that file holds OUR hand-written card
 * bullets ("Single Sign-On with SAML"), while these are the backend's own wording ("Single Sign On
 * (SAML)"). Same features, two vocabularies — merging them would silently mismatch.
 *
 * Lucide, not emoji: emoji render differently per platform, cannot inherit `currentColor`, and are
 * banned from our user-facing surfaces. These read as glyphs at 14px and follow the text colour.
 */
import {
  AlarmClock,
  BarChart3,
  Bell,
  Blocks,
  Building2,
  CalendarClock,
  Camera,
  Code2,
  FileClock,
  Gauge,
  Globe,
  KeyRound,
  Layers,
  Link2,
  ListMusic,
  Lock,
  LockKeyhole,
  MonitorCog,
  MonitorPlay,
  Palette,
  PlayCircle,
  RefreshCw,
  Repeat,
  Send,
  ShieldCheck,
  Siren,
  Smartphone,
  Sparkles,
  SquareCheckBig,
  Tags,
  Terminal,
  Type,
  UserCog,
  X,
  type LucideIcon,
} from 'lucide-react';

const PLAN_WARNING_ICONS: Record<string, LucideIcon> = {
  // Security + access
  'Single Sign On (SAML)': KeyRound,
  'Role Security': UserCog,
  'Folder Level Security': Lock,
  'Security Logging': FileClock,
  'Phone Mfa': Smartphone,
  'Api Key': KeyRound,
  'Secured Dashboard App': ShieldCheck,
  // Data + reporting
  'Dynamic Data Mapping': RefreshCw,
  'Proof Of Play': BarChart3,
  'Monitoring & Alert': Bell,
  // Brand
  'Custom Domain & Branding': Globe,
  // Device management
  'Advanced Device Management': MonitorCog,
  'Remote Control': Gauge,
  'Execute Remote Commands': Terminal,
  'Lockdown Device': LockKeyhole,
  'Screen Capture App': Camera,
  'Utils MDM': MonitorCog,
  // Workflow + org
  'Approval Workflow': SquareCheckBig,
  Teams: Building2,
  'External Com': Send,
  'Share Link': Link2,
  // Content + scheduling
  'Web Scripting App': Code2,
  'Emergency Feed App': Siren,
  'CAP Alert App': AlarmClock,
  'Advanced Schedule': CalendarClock,
  'Upload Font': Type,
  'Lockable Objects in Designer': Palette,
  'Designer Open AI Text': Sparkles,
  'Sub Playlist Control': ListMusic,
  'Content Tag': Tags,
  'Play Every': Repeat,
  'Resume On Next Play': PlayCircle,
  'Primary Zone': Layers,
  // Present for completeness — these only appear if a plan doc starts flagging them.
  Engage: MonitorPlay,
  'App Integrations': Blocks,
};

/** Never returns null, so every row keeps its glyph column and the list stays aligned. */
export function getPlanWarningIcon(description: string): LucideIcon {
  return PLAN_WARNING_ICONS[description] ?? X;
}
